import React, { createContext, useContext } from "react";
import { loadStripe } from "@stripe/stripe-js";

/*
 * STRIPE MIGRATION (Phase 1)
 * ==========================
 * This file replaces the old <StripeProvider apiKey={...}> from the deprecated
 * `react-stripe-elements` library (v6).
 *
 * The new `@stripe/stripe-js` uses loadStripe() which returns a Promise<Stripe>.
 * This promise is shared via React Context and consumed by <Elements> wrappers
 * in individual payment forms (Checkout page, UpdateSubscriptionPaymentFormModal, etc.)
 *
 * Architecture:
 *   App.js → <StripeProvider>         (this file — provides stripePromise via context)
 *     → Checkout → <Elements stripe={stripePromise}>  (wraps payment form)
 *       → CheckoutForm → useStripe() / useElements()  (accesses Stripe APIs)
 *
 * The <Elements> wrapper is configured with mode/amount/currency (deferred-intent mode)
 * at each usage site, enabling the unified <PaymentElement> with support for cards,
 * Apple Pay, Google Pay, bank transfers, and other payment methods.
 */

// Initialize Stripe once at module level — returns a Promise that resolves to the Stripe instance.
// This is the equivalent of the old <StripeProvider apiKey={...}> but loaded asynchronously.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

const StripeContext = createContext(stripePromise);

/**
 * Provides the Stripe promise to the entire app.
 * Individual payment forms wrap themselves in <Elements stripe={stripePromise}> to access
 * Stripe hooks (useStripe, useElements) within their component tree.
 */
export const StripeProvider = ({ children }) => (
  <StripeContext.Provider value={stripePromise}>
    {children}
  </StripeContext.Provider>
);

/**
 * Hook to access the Stripe promise from any component.
 * Usage:
 *   const stripePromise = useStripePromise();
 *   <Elements stripe={stripePromise}>...payment form...</Elements>
 */
export const useStripePromise = () => useContext(StripeContext);
